import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { Toaster } from "./components/ui/sonner";

import { CosmeticsProvider } from "./contexts/CosmeticsContext";

// Components
import Navbar from "./components/Navbar";
import AuthModal from "./components/AuthModal";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import GoogleAuthCallback from "./components/GoogleAuthCallback";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import PortfolioPage from "./pages/PortfolioPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ScorecardPage from "./pages/ScorecardPage";
import ChipsPage from "./pages/ChipsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ProAnalyticsPage from "./pages/ProAnalyticsPage";
import AdminDashboard from "./pages/AdminDashboard";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LeaguesPage from "./pages/LeaguesPage";
import TeacherDashboard from "./pages/TeacherDashboard";
import CreatorPage from "./pages/CreatorHub";
import PublicPortfolio from "./pages/PublicPortfolio";
import SchoolAdminDashboard from "./pages/SchoolAdminDashboard";
import ChartsPage from "./pages/ChartsPage";
import APIPage from "./pages/APIPage";
import AchievementsPage from "./pages/AchievementsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AlertsPage from "./pages/AlertsPage";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import SettingsPage from "./pages/SettingsPage";
import PlayerProfile from "./pages/PlayerProfile";
import HowScoringWorks from "./pages/HowScoringWorks";
import SponsorshipPage from "./pages/SponsorshipPage";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Main App Content
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();

  // CRITICAL: Detect Google OAuth callback session_id in URL hash synchronously during render
  // This must happen BEFORE ProtectedRoute checks to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading Trader Titans...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-slate-950 flex flex-col"
      style={{ 
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {isAuthenticated && <Navbar onAuthClick={() => setShowAuth(true)} />}
      
      <main className="flex-1 overflow-y-auto">
        <Routes>
        {/* Public routes */}
        <Route 
          path="/" 
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <>
                <Navbar onAuthClick={() => setShowAuth(true)} />
                <LandingPage onGetStarted={() => setShowAuth(true)} />
              </>
            )
          } 
        />
        
        {/* Protected routes */}
        {/* Public route for password reset */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Public route for sponsorship */}
        <Route path="/sponsorship" element={<SponsorshipPage />} />
        
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/portfolio" 
          element={
            <ProtectedRoute>
              <PortfolioPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leaderboard" 
          element={
            <ProtectedRoute>
              <LeaderboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/scorecard" 
          element={
            <ProtectedRoute>
              <ScorecardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/chips" 
          element={
            <ProtectedRoute>
              <ChipsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/subscription" 
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/subscription/success" 
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <ProAnalyticsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leagues" 
          element={
            <ProtectedRoute>
              <LeaguesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/teacher" 
          element={
            <ProtectedRoute>
              <TeacherDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creator" 
          element={
            <ProtectedRoute>
              <CreatorPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school-admin" 
          element={
            <ProtectedRoute>
              <SchoolAdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/charts" 
          element={
            <ProtectedRoute>
              <ChartsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/developer" 
          element={
            <ProtectedRoute>
              <APIPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/shop" element={<AchievementsPage />} />
        <Route path="/achievements" element={<AchievementsPage />} />
        <Route 
          path="/pro/analytics" 
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pro/alerts" 
          element={
            <ProtectedRoute>
              <AlertsPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/share/:shareCode" element={<PublicPortfolio />} />
        <Route path="/player/:userId" element={<PlayerProfile />} />
        <Route path="/how-it-works" element={<HowScoringWorks />} />
        <Route 
          path="/affiliate" 
          element={
            <ProtectedRoute>
              <AffiliateDashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/settings" 
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </main>

      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      
      {/* Toast notifications */}
      <Toaster position="top-right" theme="dark" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <AuthProvider>
          <CosmeticsProvider>
            <AppContent />
          </CosmeticsProvider>
        </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}

export default App;
